import { useEffect, useMemo, useState } from 'react'
import { Check, ChevronDown, ChevronLeft, ChevronRight } from '../../icons'
import { Button } from '../button/button'
import {
  Popover,
  PopoverContent,
  PopoverItem,
  PopoverScrollArea,
  PopoverSearch,
  PopoverSection,
  PopoverTrigger,
} from '../popover/popover'

type Locale = 'en' | 'zh'
/** `[slug, parent, English, Chinese, Shopify id]`, in the tree's own order. */
type Row = [slug: string, parent: string | null, en: string, zh: string, shopify: string | null]

export interface CategoryTable {
  rows: Row[]
  bySlug: Map<string, Row>
  children: Map<string | null, Row[]>
  /** Each row's names lowercased, English then Chinese, for search. */
  lower: [string, string][]
}

export interface CategoryLabels {
  label: string
  placeholder: string
  search: string
  empty: string
  loading: string
  back: string
  /** The row that picks the category being browsed, rather than one below it. */
  whole: (name: string) => string
  /** Said once the limit is reached. */
  full: (max: number) => string
}

let loading: Promise<CategoryTable> | undefined
let cachedTable: CategoryTable | null = null

/**
 * The category tree: Shopify's product taxonomy and ondit's additions, the same
 * file the server seeds from (`catalog::categories`). Loaded once, and only by
 * a screen that shows a picker — fourteen thousand categories in two languages
 * are 1.5 MB before compression.
 */
export function loadCategories(): Promise<CategoryTable> {
  loading ??= import('../../../../content/categories.json')
    .then(({ default: file }) => {
      const rows = file.nodes as Row[]
      const children = new Map<string | null, Row[]>()
      for (const row of rows) {
        const siblings = children.get(row[1])
        if (siblings) siblings.push(row)
        else children.set(row[1], [row])
      }
      cachedTable = {
        rows,
        bySlug: new Map(rows.map((row) => [row[0], row])),
        children,
        lower: rows.map((row) => [row[2].toLowerCase(), row[3].toLowerCase()]),
      }
      return cachedTable
    })
    .catch((error) => {
      loading = undefined
      throw error
    })
  return loading
}

/** The tree once it has loaded, and null until then. */
export function useCategories(): CategoryTable | null {
  const [table, setTable] = useState<CategoryTable | null>(cachedTable)
  useEffect(() => {
    let live = true
    loadCategories().then((loaded) => live && setTable(loaded))
    return () => {
      live = false
    }
  }, [])
  return table
}

const nameOf = (row: Row, locale: Locale) => (locale === 'zh' ? row[3] : row[2])

/** A category in the reader's language, or null for a slug the tree does not have. */
export function categoryName(
  table: CategoryTable | null,
  slug: string,
  locale: Locale
): string | null {
  const row = table?.bySlug.get(slug)
  return row ? nameOf(row, locale) : null
}

/** Everything above a category, root first. */
function ancestors(table: CategoryTable, row: Row): Row[] {
  const above: Row[] = []
  for (
    let at = row[1] ? table.bySlug.get(row[1]) : undefined;
    at;
    at = at[1] ? table.bySlug.get(at[1]) : undefined
  )
    above.unshift(at)
  return above
}

/**
 * The categories a search finds, best first.
 *
 * Substrings in either language rather than a fuzzy score, which let "6201"
 * find 2610 in the picker this replaced: an exact name, then a name that
 * starts with the search, then one containing it, then one holding every word.
 * Within a tier the shorter name first, as the more general, then the tree's
 * order.
 */
function search(table: CategoryTable, text: string, limit: number): Row[] {
  const query = text.trim().toLowerCase()
  const words = query.split(/\s+/).filter(Boolean)
  const found: { row: Row; tier: number; order: number }[] = []
  table.rows.forEach((row, order) => {
    const names = table.lower[order]
    const tier = names.some((name) => name === query)
      ? 0
      : names.some((name) => name.startsWith(query))
        ? 1
        : names.some((name) => name.includes(query))
          ? 2
          : words.length > 1 && words.every((word) => names.some((name) => name.includes(word)))
            ? 3
            : -1
    if (tier >= 0) found.push({ row, tier, order })
  })
  return found
    .sort((a, b) => a.tier - b.tier || a.row[2].length - b.row[2].length || a.order - b.order)
    .slice(0, limit)
    .map((match) => match.row)
}

/**
 * The category picker: several categories, each at any level of the tree.
 *
 * The tree is browsed a level at a time until somebody types — fourteen
 * thousand rows are not a list anyone scans — and every level offers the
 * category being browsed itself, so a brand that sells all of Apparel is not
 * made to pick fifty kinds of it. The field shows only the chosen names.
 *
 * The popover owns its own search state, so `text` here is only what the rows
 * are filtered by; `PopoverSearch` reports each keystroke through
 * `onValueChange`.
 */
export function CategorySelect({
  id,
  value,
  onChange,
  locale,
  labels,
  max = 5,
  disabled,
}: {
  id?: string
  /** Slugs, in the order chosen. */
  value: string[]
  onChange: (slugs: string[]) => void
  locale: Locale
  labels: CategoryLabels
  max?: number
  disabled?: boolean
}) {
  const table = useCategories()
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const [at, setAt] = useState<string | null>(null)
  const full = value.length >= max
  const found = useMemo(
    () => (table && text.trim() ? search(table, text, 50) : null),
    [table, text]
  )
  const here = table && at ? table.bySlug.get(at) : undefined
  const names = table ? value.map((slug) => categoryName(table, slug, locale) ?? slug) : []

  const toggle = (slug: string) => {
    if (value.includes(slug)) onChange(value.filter((chosen) => chosen !== slug))
    else if (!full) onChange([...value, slug])
  }
  const path = (row: Row) =>
    table
      ? ancestors(table, row)
          .map((above) => nameOf(above, locale))
          .join(' › ')
      : ''
  const item = (row: Row, detail: string | null, opens: boolean, key = row[0]) => {
    const chosen = value.includes(row[0])
    return (
      <PopoverItem
        key={key}
        active={chosen}
        disabled={!opens && !chosen && full}
        onClick={() => (opens ? setAt(row[0]) : toggle(row[0]))}
      >
        <span className='min-w-0 flex-1'>
          <span className='block'>{nameOf(row, locale)}</span>
          {detail && <span className='block truncate text-[var(--text-muted)]'>{detail}</span>}
        </span>
        {chosen && <Check aria-hidden className='size-[14px]' />}
        {opens && <ChevronRight aria-hidden className='size-[14px] text-[var(--text-icon)]' />}
      </PopoverItem>
    )
  }

  return (
    <Popover
      open={open && !disabled}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) {
          setText('')
          setAt(null)
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant='default'
          className='h-auto w-full min-w-0 justify-between py-1'
          disabled={disabled}
          aria-label={`${labels.label}: ${names.join(', ') || labels.placeholder}`}
          aria-expanded={open}
        >
          {names.length > 0 ? (
            <span className='flex min-w-0 flex-wrap gap-1'>
              {names.map((name, i) => (
                <span key={value[i]} className='rounded-sm bg-[var(--surface-active)] px-2'>
                  {name}
                </span>
              ))}
            </span>
          ) : (
            <span className='truncate text-[var(--text-muted)]'>
              {table || value.length === 0 ? labels.placeholder : labels.loading}
            </span>
          )}
          <ChevronDown aria-hidden className='size-[14px]' />
        </Button>
      </PopoverTrigger>
      {/* Keyed by the level so the highlight starts on the new level's first row
          rather than pointing at one that has gone. */}
      <PopoverContent key={at ?? ''} align='start' minWidth={280} className='p-0'>
        <PopoverSearch placeholder={labels.search} onValueChange={setText} />
        <PopoverScrollArea>
          {!table ? (
            <p className='py-6 text-center text-sm text-[var(--text-muted)]'>{labels.loading}</p>
          ) : found ? (
            found.length > 0 ? (
              found.map((row) => item(row, path(row), false))
            ) : (
              <p className='py-6 text-center text-sm text-[var(--text-muted)]'>{labels.empty}</p>
            )
          ) : (
            <>
              {!here && value.length > 0 && (
                <>
                  {value
                    .map((slug) => table.bySlug.get(slug))
                    .filter((row): row is Row => row !== undefined)
                    .map((row) => item(row, path(row), false, `-chosen-${row[0]}`))}
                </>
              )}
              {here && (
                <>
                  <PopoverSection>
                    {[...ancestors(table, here), here]
                      .map((row) => nameOf(row, locale))
                      .join(' › ')}
                  </PopoverSection>
                  <PopoverItem onClick={() => setAt(here[1])}>
                    <ChevronLeft aria-hidden className='size-[14px]' />
                    <span className='flex-1'>{labels.back}</span>
                  </PopoverItem>
                  <PopoverItem
                    active={value.includes(here[0])}
                    disabled={!value.includes(here[0]) && full}
                    onClick={() => toggle(here[0])}
                  >
                    <span className='flex-1'>{labels.whole(nameOf(here, locale))}</span>
                    {value.includes(here[0]) && <Check aria-hidden className='size-[14px]' />}
                  </PopoverItem>
                </>
              )}
              {(table.children.get(at) ?? []).map((row) =>
                item(row, null, table.children.has(row[0]))
              )}
            </>
          )}
        </PopoverScrollArea>
        {full && (
          <p className='border-[var(--border)] border-t px-3 py-2 text-sm text-[var(--text-muted)]'>
            {labels.full(max)}
          </p>
        )}
      </PopoverContent>
    </Popover>
  )
}
